/**
 * Semantic tones — STATUS = COLOUR.
 *
 * The handoff uses Radix-style ramps (`--blue-blue9/3/11`) resolved via
 * `color-mix`. React Native has no `color-mix`, so each tone is precomputed as
 * a solid + a soft rgba tint + a readable text colour. The soft tints are
 * translucent so they read correctly over both light and dark card surfaces.
 *
 * `text` is a Tamagui theme TOKEN (`$tone_<k>_text`, values in ./brand
 * toneTextLight/Dark) so it stays readable in both themes — pass it only to a
 * Tamagui `color` prop or <Icon color>. For foreground use `.text`, not `.solid`
 * (solid is a fill: fixed hex, too dark as text on the dark theme).
 */
export type Tone = 'blue' | 'green' | 'gold' | 'red' | 'purple' | 'slate' | 'default'

export interface ToneColors {
  solid: string
  soft: string
  text: string
}

export const TONES: Record<Tone, ToneColors> = {
  blue: { solid: '#2F6DB5', soft: 'rgba(47,109,181,0.13)', text: '$tone_blue_text' },
  green: { solid: '#2F8F6B', soft: 'rgba(47,143,107,0.13)', text: '$tone_green_text' },
  gold: { solid: '#B0863C', soft: 'rgba(176,134,60,0.15)', text: '$tone_gold_text' },
  red: { solid: '#C0584A', soft: 'rgba(192,88,74,0.13)', text: '$tone_red_text' },
  purple: { solid: '#1D4289', soft: 'rgba(29,66,137,0.11)', text: '$tone_purple_text' },
  slate: { solid: '#64748B', soft: 'rgba(100,116,139,0.13)', text: '$tone_slate_text' },
  default: { solid: '#64748B', soft: 'rgba(100,116,139,0.10)', text: '$tone_slate_text' },
}

export const tone = (k?: string): ToneColors => TONES[(k as Tone) ?? 'default'] ?? TONES.default

/** Always-dark "chrome" (sidebar / login) — fixed navy + bronze accent. */
export const CHROME = {
  navy: '#13245A',
  navyTop: '#16265C',
  navyBottom: '#101F4C',
  bronze: '#D8BE88', // active accent on navy
  bronzeSolid: '#C2974E',
  textActive: '#FFFFFF',
  textIdle: 'rgba(255,255,255,0.66)',
  iconIdle: 'rgba(255,255,255,0.60)',
  hover: 'rgba(216,190,136,0.18)',
  section: 'rgba(255,255,255,0.36)',
  divider: 'rgba(255,255,255,0.10)',
  badgeIdle: 'rgba(255,255,255,0.14)',
} as const
